import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject } from 'rxjs';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { FbUser } from '../_interfaces/user.interface';
import {
  convertTimeStampsFb_Js,
  convertTimeStampsJs_Fb,
  getNewId,
} from '../_utils/utils';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Config } from '../_interfaces/config.interface';
import { FbEntity, JsEntity } from '../_types/entity.type';
import { Collection } from '../_types/collection.type';
import { Functions, httpsCallable, connectFunctionsEmulator } from '@angular/fire/functions';
import { environment } from '../../environments/environment';

interface keyValueType {
  [key: string]: string | number | boolean | Date | null;
}

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  currentUserSubject$ = new BehaviorSubject<any>({});
  public currentUser$: Observable<any> =
    this.currentUserSubject$.asObservable();
  configSubject$ = new BehaviorSubject<Config[]>([]);
  public config$: Observable<Config[]> = this.configSubject$.asObservable();

  public users: any[] = [];
  public orderedUserIds: string[] = [];
  public currentUser!: any;
  public uidUserMap: any = {};
  public tagNameToUidMap: { [key: string]: string } = {};
  public uidToName: { [key: string]: string } = {};
  unSubscribe = new Subject<void>();

  constructor(
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private functions: Functions
  ) {
    // Connect to Firebase Functions emulator for local testing
    // Set useEmulator: true in environment.ts to use local emulator
    if ((environment as any).useEmulator) {
      connectFunctionsEmulator(this.functions, 'localhost', 5001);
      console.log('Connected to Functions emulator on port 5001');
    }
  }

  async callableFunction(funcName: string, data: any, options?: { timeout?: number }) {
    try {
      const callableFunction = httpsCallable(this.functions, funcName, options);
      const result = await callableFunction(data);
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  getFbUserData(): Observable<FbUser[]> {
    return this.afs.collection<FbUser>('users').valueChanges();
  }

  getConfig<T extends FbEntity, K extends JsEntity>(): Observable<Config[]> {
    return this.afs
      .collection<T>('config')
      .valueChanges()
      .pipe(
        map((items: T[]) => {
          const config = items.map((item) => convertTimeStampsFb_Js(item) as K);
          this.configSubject$.next((config as unknown as Config[]) || []);
          return config;
        })
      ) as unknown as Observable<Config[]>;
  }

  getUserNameFromId(id: string | undefined): string | undefined {
    if (!id) return;
    return this.users.find((user) => user.value === id)?.label;
  }

  getDisplayName(id: string | null): string {
    if (!id) return '';
    const name = this.users.find((user) => user.value === id)?.label;
    if (name && name.split(' ').length > 1) {
      return name.split(' ')[0];
    } else {
      return 'INVALID';
    }
  }

  getUserFirstAndLastLetter(id: string) {
    const nameParts = this.getUserNameFromId(id)?.trim().split(' ') || '';

    if (nameParts.length === 0) {
      return '';
    }
    const firstNameFirstLetter = nameParts[0].charAt(0);

    if (nameParts.length === 1) {
      return firstNameFirstLetter;
    }

    const lastNameFirstLetter = nameParts[nameParts.length - 1].charAt(0);
    return firstNameFirstLetter + lastNameFirstLetter;
  }

  getCurrentUserId() {
    return this.getLocalUser()?.uid;
  }

  getCurrentUser() {
    const uid = this.getCurrentUserId();
    if (uid) {
      return this.users.find((user) => user.value === uid);
    }
  }

  downloadFileAsBlob(filePath: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const fileRef = this.storage.ref(filePath);
      fileRef.getDownloadURL().subscribe(
        (url) => {
          console.log('Download URL:', url);
          // Try fetching the file without the token
          fetch(url, { mode: 'cors' })
            .then((response) => response.blob())
            .then((blob) => {
              resolve(blob);
              console.log('Blob:', blob);
            })
            .catch((error) => {
              reject(error);
              console.error('Error fetching file:', error);
            });
        },
        (error) => {
          reject(error);
          console.error('Error getting download URL:', error);
        }
      );
    });
  }

  getAllItems<T extends FbEntity, K extends JsEntity>(
    collection: Collection,
    from?: Date
  ) {
    return this.afs
      .collection<T>(collection, (ref) => {
        let condition = ref.where(
          'cloudUpdatedAt',
          '>',
          Timestamp.fromDate(from || new Date(0))
        );
        return condition;
      })
      .valueChanges()
      .pipe(
        map((items: T[]) => {
          return items.map((item) => convertTimeStampsFb_Js(item) as K);
        })
      );
  }

  getItems<T extends FbEntity, K extends JsEntity>(
    criteria: keyValueType,
    collection: Collection,
    limit?: number
  ) {
    return this.afs
      .collection<T>(collection, (ref) => {
        let condition = ref
          .where('deletedAt', '==', null)
          .orderBy('createdAt', 'desc');
        for (const [key, value] of Object.entries(criteria)) {
          condition = condition.where(key, '==', value);
        }
        if (limit) condition = condition.limit(limit);
        return condition;
      })
      .valueChanges()
      .pipe(
        map((items: T[]) => {
          return items.map((item) => convertTimeStampsFb_Js(item) as K);
        })
      );
  }

  async createItems<T extends JsEntity>(items: T[], collection: Collection) {
    const uid = this.getLocalUser()?.uid;
    if (!items.length || !uid) return;
    // Flesh details
    items.forEach((item) => {
      item.id = item.id || getNewId();
      item.uid = collection === 'users' ? item.uid : uid;
      item.createdBy = uid;
      item.createdAt = item.createdAt || new Date();
      item.cloudUpdatedAt = serverTimestamp();
    });

    // Add in Firestore - Because of the max limit of 500 writes per batch
    if (items.length > 10 && items.length < 450) {
      const batch = this.afs.firestore.batch();
      items.forEach(async (item) => {
        const docRef = this.afs.firestore.collection(collection).doc(item.id);
        batch.set(docRef, convertTimeStampsJs_Fb(item)!, { merge: true });
      });
      await batch.commit();
    } else {
      items.forEach(async (item) => {
        await this.afs
          .collection(collection)
          .doc(item.id)
          .set(convertTimeStampsJs_Fb(item));
      });
    }
  }

  async updateItems<T extends JsEntity>(items: T[], collection: Collection) {
    const uid = this.getLocalUser()?.uid;
    if (!items.length || !uid) return;
    // Flesh details
    items.forEach((item) => {
      item.uid = collection === 'users' ? item.uid : uid;
      item.updatedAt = new Date();
      item.cloudUpdatedAt = serverTimestamp();
    });
    // Add in Firestore - Because of the max limit of 500 writes per batch
    if (items.length > 10 && items.length < 450) {
      const batch = this.afs.firestore.batch();
      items.forEach((item) => {
        const docRef = this.afs.firestore.collection(collection).doc(item.id);
        batch.update(docRef, convertTimeStampsJs_Fb(item)!);
      });
      await batch.commit();
    } else {
      items.forEach(async (item) => {
        await this.afs
          .collection(collection)
          .doc(item.id)
          .update(convertTimeStampsJs_Fb(item)!);
      });
    }
  }

  async uploadFiles(files: File[], pathExceptFileName: string) {
    if (!files.length) return;
    const uid = this.getLocalUser()?.uid;
    if (!uid) return;
    const uploadTasks = files.map((file) => {
      const filePath = `${pathExceptFileName}${file.name}`;
      const fileRef = this.storage.ref(filePath);
      return this.storage.upload(filePath, file);
    });
    return await Promise.all(uploadTasks);
  }

  async deleteFiles(fileNames: string[], pathExceptFileName: string) {
    if (!fileNames.length) return;
    const uid = this.getLocalUser()?.uid;
    if (!uid) return;
    const deleteTasks = fileNames.map((fileName) => {
      const filePath = `${pathExceptFileName}${fileName}`;
      const fileRef = this.storage.ref(filePath);
      return fileRef.delete();
    });
    return await Promise.all(deleteTasks);
  }

  getLocalUser() {
    return JSON.parse(localStorage.getItem('user')!);
  }

  getUsername(uid: string) {
    return this.users.find((user) => user.value === uid)?.label;
  }

  checkRoleForCurrentUser(role: string) {
    return this.getCurrentUser()?.roles.includes(role);
  }

  isAdmin(): boolean {
    return this.getCurrentUser()?.roles.includes('ADMIN');
  }

  stopSubscription() {
    this.unSubscribe?.next();
    this.unSubscribe?.complete();
  }

  /**
   * Get template by name using the getTemplateByName callable function.
   * Returns an Observable for compatibility with existing component code.
   */
  getTemplate(params: { templateName: string }): Observable<any> {
    return new Observable((observer) => {
      this.callableFunction('getTemplateByName', params)
        .then((result: any) => {
          if (result.success && result.data) {
            observer.next(result.data.Template);
          } else {
            observer.error(
              new Error(result.message || 'Failed to get template')
            );
          }
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
          observer.complete();
        });
    });
  }

  /**
   * Send a test email using the sendTestEmail callable function.
   * Returns an Observable for compatibility with existing component code.
   *
   * source: 'editor' = Always use raw HTML content (for drafts/unpublished templates)
   * source: 'published' = Always use AWS SES template (for production templates)
   */
  sendEmail(emailRequest: {
    recipient: {
      email: string;
      variables?: Record<string, string | number>;
      langCode: string;
    };
    templateName: string;
    templateVariables?: Record<string, string | number>;
    // Explicit source flag - determines how email is sent
    source: 'editor' | 'published';
    // Required when source is 'editor'
    htmlContent?: string;
    subjectLine?: string;
  }): Observable<any> {
    return new Observable((observer) => {
      // Extract base template name (remove language suffix if present)
      let baseTemplateName = emailRequest.templateName;
      const langCode = emailRequest.recipient.langCode;

      // If templateName already has -langCode suffix, extract the base name
      if (baseTemplateName.endsWith(`-${langCode}`)) {
        baseTemplateName = baseTemplateName.slice(0, -(langCode.length + 1));
      }

      // Merge recipient variables with template variables
      const templateData = {
        ...emailRequest.templateVariables,
        ...emailRequest.recipient.variables,
      };

      const sendTestEmailRequest: any = {
        recipientEmail: emailRequest.recipient.email,
        templateName: baseTemplateName,
        languageCode: langCode,
        templateData,
        // Explicit source flag - backend will use this to determine behavior
        source: emailRequest.source,
      };

      // Only include raw HTML content when source is 'editor'
      if (emailRequest.source === 'editor') {
        if (emailRequest.htmlContent) {
          sendTestEmailRequest.htmlContent = emailRequest.htmlContent;
        }
        if (emailRequest.subjectLine) {
          sendTestEmailRequest.subjectLine = emailRequest.subjectLine;
        }
      }

      this.callableFunction('sendTestEmail', sendTestEmailRequest)
        .then((result: any) => {
          if (result.success) {
            // Return format compatible with existing component expectations
            observer.next({
              MessageId: result.data?.messageId,
              templateUsed: result.data?.templateUsed,
            });
          } else {
            observer.next({
              status: 'error',
              message: result.message || 'Failed to send email',
            });
          }
          observer.complete();
        })
        .catch((error) => {
          observer.error(error);
          observer.complete();
        });
    });
  }
}
